import { useState } from 'react';
import {
  formatDate,
  IconEdit,
  IconRefresh,
  IconChangeLogUpdated,
  IconExpand,
  IconCollapse,
  IconLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import { AmlChangeLogTypes } from './types';
import { AmlAction, ClientScoreTypeEnum, CustomerScore } from '../types';
import { useLazyGetCustomersAMLScoresHistoryByIdQuery } from '../store/services/customers';
import { useLazyGetApplicationsAMLScoresHistoryByIdQuery } from '../../new-application/store/services';
import AmlChangeLogDetails from './change-log-details';

import cn from './styles.module.css';

const AmlChangeLog = ({
  amlChangeLog,
  evenChangeLog,
  userName,
  type,
  organizationId,
  applicationId,
}: AmlChangeLogTypes) => {
  const [collapsed, setCollapsed] = useState(false);
  const [amlChangeLogDetails, setAmlChangeLogDetails] = useState<
    CustomerScore | undefined
  >(undefined);
  const [evenChangeLogDetails, setEvenChangeLogDetails] = useState<
    CustomerScore | undefined
  >(undefined);

  const { riskScoreRanges } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [getCustomerAmlScoresHistoryById, { isLoading, isFetching }] =
    useLazyGetCustomersAMLScoresHistoryByIdQuery();
  const [
    getApplicationAmlScoresHistoryById,
    {
      isLoading: applicationAmlDetailsIsLoading,
      isFetching: applicationAmlDetailsIsFetching,
    },
  ] = useLazyGetApplicationsAMLScoresHistoryByIdQuery();

  const onCollapse = async () => {
    if (organizationId) {
      const almResponse = (await getCustomerAmlScoresHistoryById({
        id: organizationId,
        scoreId: amlChangeLog.id,
      })) as { data: { data: CustomerScore } };

      if (almResponse?.data?.data) {
        setAmlChangeLogDetails(almResponse?.data?.data);
      }

      if (evenChangeLog) {
        const eventReponse = (await getCustomerAmlScoresHistoryById({
          id: organizationId,
          scoreId: evenChangeLog.id,
        })) as { data: { data: CustomerScore } };

        if (eventReponse?.data?.data) {
          setEvenChangeLogDetails(eventReponse?.data?.data);
        }
      }
    } else {
      const almResponse = (await getApplicationAmlScoresHistoryById({
        id: applicationId as string,
        scoreId: amlChangeLog.id,
      })) as { data: { data: CustomerScore } };

      if (almResponse?.data?.data) {
        setAmlChangeLogDetails(almResponse?.data?.data);
      }

      if (evenChangeLog) {
        const eventReponse = (await getApplicationAmlScoresHistoryById({
          id: applicationId as string,
          scoreId: evenChangeLog.id,
        })) as { data: { data: CustomerScore } };

        if (eventReponse?.data?.data) {
          setEvenChangeLogDetails(eventReponse?.data?.data);
        }
      }
    }
  };

  let evenTotalRiskClassName: string;
  let amlTotalRiskClassName: string;

  const evenRiskLevel = riskScoreRanges?.find(
    (item) =>
      (evenChangeLog?.totalRiskScore as number) >= item.rangeStart &&
      (evenChangeLog?.totalRiskScore as number) <= item.rangeEnd,
  );

  const amlRiskLevel = riskScoreRanges?.find(
    (item) =>
      amlChangeLog?.totalRiskScore >= item.rangeStart &&
      amlChangeLog?.totalRiskScore <= item.rangeEnd,
  );

  if (evenRiskLevel?.action === AmlAction.NONE) {
    evenTotalRiskClassName = cn.totalRiskScoringGood;
  } else if (evenRiskLevel?.action === AmlAction.OPS_REVIEW) {
    evenTotalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    evenTotalRiskClassName = cn.totalRiskScoringLow;
  }

  if (amlRiskLevel?.action === AmlAction.NONE) {
    amlTotalRiskClassName = cn.totalRiskScoringGood;
  } else if (amlRiskLevel?.action === AmlAction.OPS_REVIEW) {
    amlTotalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    amlTotalRiskClassName = cn.totalRiskScoringLow;
  }

  return (
    <div className={cn.amlChangeLog}>
      <div className={cn.amlChangeLogTop}>
        <div className={cn.amlChangeLogDate}>
          {formatDate(amlChangeLog.createdAt, true)}
        </div>
        {amlChangeLog?.employeeId && (
          <div className={cn.amlChangeLogUser}>{userName}</div>
        )}
        {amlChangeLog?.clientScoreType === ClientScoreTypeEnum.AUTO && (
          <div className={cn.amlChangeLogType}>AUTO</div>
        )}
      </div>
      <div className={cn.amlChangeLogContent}>
        <button
          type="button"
          className={cn.amlChangeLogButton}
          onClick={() => {
            setCollapsed(!collapsed);

            if (!collapsed && !amlChangeLogDetails) {
              onCollapse();
            }
          }}
          disabled={
            amlChangeLog.clientScoreType === ClientScoreTypeEnum.MANUAL &&
            evenChangeLog?.clientScoreType === ClientScoreTypeEnum.MANUAL
          }
          aria-label="Collapse Button"
        >
          <div className={cn.amlChangeLogButtonLeft}>
            {evenChangeLog ? (
              <div className={cn.amlChangeLogPrev}>
                <div
                  className={`${cn.amlChangeLogRiskScore} ${evenTotalRiskClassName}`}
                >
                  {evenChangeLog.totalRiskScore}
                </div>
                <div className={cn.amlChangeLogType}>
                  {evenChangeLog?.clientScoreType ===
                  ClientScoreTypeEnum.AUTO ? (
                    <span>
                      <IconRefresh width={14} height={14} /> auto
                    </span>
                  ) : (
                    <span>
                      <IconEdit width={14} height={14} /> manually
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn.amlPrevEmpty} />
            )}
            <div className={cn.amlChangeLogIconWrapper}>
              <IconChangeLogUpdated />
            </div>
            {amlChangeLog ? (
              <div className={cn.amlChangeLogPrev}>
                <div
                  className={`${cn.amlChangeLogRiskScore} ${amlTotalRiskClassName}`}
                >
                  {amlChangeLog.totalRiskScore}
                </div>
                <div className={cn.amlChangeLogType}>
                  {amlChangeLog?.clientScoreType ===
                  ClientScoreTypeEnum.AUTO ? (
                    <span>
                      <IconRefresh width={14} height={14} /> auto
                    </span>
                  ) : (
                    <span>
                      <IconEdit width={14} height={14} /> manually
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn.amlPrevEmpty} />
            )}
          </div>
          {(amlChangeLog.clientScoreType === ClientScoreTypeEnum.AUTO ||
            evenChangeLog?.clientScoreType === ClientScoreTypeEnum.AUTO) && (
            <div className={cn.amlChangeLogButtonRight}>
              <div className={cn.amlChangeLogButtonRightText}>Details</div>
              <div className={cn.amlChangeLogButtonRightIcon}>
                {!collapsed ? (
                  <IconExpand width={16} height={16} />
                ) : (
                  <IconCollapse width={16} height={16} />
                )}
              </div>
            </div>
          )}
        </button>
        {collapsed && (
          <div className={cn.changeLogsDetails}>
            {isLoading ||
            isFetching ||
            applicationAmlDetailsIsLoading ||
            applicationAmlDetailsIsFetching ? (
              <div className={cn.changeLogDetailsLoader}>
                <IconLoader />
              </div>
            ) : (
              <div className={cn.changeLogsDetailsContent}>
                {evenChangeLogDetails ? (
                  <AmlChangeLogDetails
                    changeLog={evenChangeLogDetails}
                    type={type}
                    className={evenTotalRiskClassName}
                    riskLevel={evenRiskLevel}
                  />
                ) : (
                  <div className={cn.changeLogDetailsEmpty}>
                    <div />
                  </div>
                )}
                <IconChangeLogUpdated
                  className={cn.changeLogsDetailsContentArrow}
                />
                {amlChangeLogDetails ? (
                  <AmlChangeLogDetails
                    changeLog={amlChangeLogDetails}
                    type={type}
                    className={amlTotalRiskClassName}
                    riskLevel={amlRiskLevel}
                  />
                ) : (
                  <div className={cn.changeLogDetailsEmpty}>
                    <div />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmlChangeLog;
